<script setup lang="ts">
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import type { ChampionshipRegistrationWaitingListTask } from "@/model/championship/championship-registration-waiting-list-task"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { useHttpClient, useHttpGet } from "@/vf"
import { computed, ref, toRefs } from "vue"
import ChampionshipRegistrationApprovalStateList from "./ChampionshipRegistrationApprovalStateList.vue"
import ChampionshipRegistrationTeamList from "./ChampionshipRegistrationTeamList.vue"
import type { ChampionshipRegistrationSelectTeamData } from "./championship-registration"

const props = defineProps<TasksProps<ChampionshipRegistrationWaitingListTask>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const { data, refresh } = await useHttpGet<ChampionshipRegistrationSelectTeamData>(
    `/championship/registration/${task.value.organizationId}/waiting-list/${task.value.id}`,
)
const http = useHttpClient()

const waitingTeams = computed(() =>
    data.value.teams.filter(
        team => team.approvalState === "waiting_list" || team.approvalState === "waiting_list_approved",
    ),
)

/*─────────────────────────────────────┐
│  submit                              │
└─────────────────────────────────────*/
const submitting = ref(false)
async function submit(allTeamsDecided: boolean, approvalStates: Record<string, string>) {
    if (!allTeamsDecided) {
        return
    }

    submitting.value = true
    try {
        await http.post(`/championship/registration/${data.value.registrationId}/waiting-list`, {
            teams: approvalStates,
        })
    } finally {
        await refresh()
        emit("updated")
        submitting.value = false
    }
}

/*─────────────────────────────────────┐
│  accept waiting list approval        │
└─────────────────────────────────────*/
async function acceptWaitingListApproval(team: ChampionshipRegistrationTeam) {
    submitting.value = true
    try {
        await http.post(
            `/championship/registration/${data.value.registrationId}/waiting-list/accept-waiting-list-approval`,
            { teamIds: [team.id] },
        )
        await refresh()
        emit("updated")
    } finally {
        submitting.value = false
    }
}
</script>
<template>
    <div class="p-5">
        <div v-if="viewMode === 'customer'">
            <div class="alert alert-info mb-5 mt-3">
                {{ $t("@tasks:tasks.championship_registration.approve.approval_pending") }}
            </div>

            <ChampionshipRegistrationTeamList :teams="waitingTeams" :view-mode="props.viewMode">
                <template #secondaryRow="{ team, colspan }">
                    <tr v-if="team.approvalState === 'waiting_list_approved'">
                        <td :colspan="colspan" class="bg-white px-5 py-4">
                            <div class="mb-4">
                                {{
                                    $t(
                                        "@tasks:tasks.championship_registration.waiting_list.office_approved_waiting_for_organization.prompt",
                                    )
                                }}
                            </div>

                            <button
                                type="button"
                                @click="acceptWaitingListApproval(team)"
                                class="btn btn-primary"
                                :disabled="submitting"
                            >
                                {{
                                    $t(
                                        "@tasks:tasks.championship_registration.waiting_list.office_approved_waiting_for_organization.button",
                                    )
                                }}
                            </button>
                        </td>
                    </tr>
                </template>
            </ChampionshipRegistrationTeamList>
        </div>
        <div v-else>
            <ChampionshipRegistrationApprovalStateList
                :data="data"
                :teams="waitingTeams"
                @updated="refresh()"
                approve-state="waiting_list_approved"
                :view-mode="props.viewMode"
            >
                <template #bottom="{ allTeamsDecided, approvalStates }">
                    <div class="d-flex justify-content-end">
                        <button
                            class="btn btn-primary mt-5"
                            :disabled="!allTeamsDecided || submitting"
                            @click="submit(allTeamsDecided, approvalStates)"
                        >
                            {{ $t("crud:button.save") }}
                        </button>
                    </div>
                </template>
            </ChampionshipRegistrationApprovalStateList>
        </div>
        <!-- <ChampionshipRegistrationTeamList -->
        <!--     :teams="waitingTeams" -->
        <!--     :show-price="true" -->
        <!--     :show-edit="false" -->
        <!--     :show-delete="false" -->
        <!--     :data="data" -->
        <!--     @updated="refresh()" -->
        <!-- /> -->
    </div>
</template>
